import SelectPatient from '../../components/create-claim/SelectPatient';
import ImportCashlessClaims from '../../components/create-claim/ImportCashlessClaims';
import BasicInformation from '@/components/create-claim/BasicInformation';
import BitMoreInformation from '@/components/create-claim/BitMoreDetails';
import OrgUnderEnrollment from '@/components/create-claim/OrgUnderEnrollment';
import TreatmentDetails from '@/components/create-claim/TreatmentDetails';

import useClaimCreateStore, {
  EClaimCreateFlowSteps,
  IClaimCreateFormSchema,
} from '@/store/claimCreateStore';
import { useEffect, useState } from 'react';
import NetworkHospitalsV2 from '../network-hospitals/v2';
import NetworkHospitalsV1 from '../network-hospitals/v1';
import SelectClaimType from '@/components/create-claim/SelectClaimType';
import { useAppContext } from '@/context/AppContext';
import {
  EBenefitCategory,
  ECareType,
  EClaimType,
  EPatientRelationship,
  IClaimDetails,
} from '@/types';
import { z } from 'zod';
import toast from 'react-hot-toast';
import ClaimCreated from '@/components/create-claim/ClaimCreated';
import { IClaimCreatePayload } from '@/queries/claims.queries';
import { getDimensions, track } from '@/utils';
import useDuration from '@/hooks/useDuration';
import { useSearchParams } from 'react-router-dom';
import { IClaimCreationStats } from '@/utils/tracking';
import { TRACKER_EVENTS } from '@/constants';
import { getRequestCallbackStorageKey } from '@/context';

const ScreenFlowStepMap = {
  // Common Steps
  [EClaimCreateFlowSteps.SELECT_CLAIM_TYPE]: SelectClaimType,
  [EClaimCreateFlowSteps.ORG_UNDER_ENROLLMENT]: OrgUnderEnrollment,
  [EClaimCreateFlowSteps.CLAIM_CREATED]: ClaimCreated,
  // Cashless Claim Flow Steps
  [EClaimCreateFlowSteps.SELECT_PATIENT]: SelectPatient,
  [EClaimCreateFlowSteps.CLAIM_FOUND]: ImportCashlessClaims,
  [EClaimCreateFlowSteps.COLLECT_BASIC_INFO]: BasicInformation,
  [EClaimCreateFlowSteps.COLLECT_MORE_INFO]: BitMoreInformation,
  [EClaimCreateFlowSteps.NETWORK_HOSPITAL_V1]: NetworkHospitalsV1,
  [EClaimCreateFlowSteps.NETWORK_HOSPITAL_V2]: NetworkHospitalsV2,
  // Plum OPD Claim Flow Steps
  [EClaimCreateFlowSteps.TREATMENT_DETAILS]: TreatmentDetails,
};

export interface IClaimCreateStepProps {
  isInCreateFlow?: boolean;
  submitFlow?: (formState: IClaimCreateFormSchema) => void;
  createClaimPayload?: IClaimCreatePayload;
  startTimer?: () => void;
  pauseTimer?: () => number | null;
  trackCreation?: (
    data: IClaimDetails | null,
    attrs: Pick<IClaimCreationStats, 'creationMode'>
  ) => void;
}

const CreateCashlessClaim = () => {
  const currentStep = useClaimCreateStore((state) => state.currentStep);
  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);
  const memberDetails = useClaimCreateStore((state) => state.memberDetails);
  const firstMember = memberDetails?.length > 0 ? memberDetails[0] : undefined;

  const [createClaimPayload, setCreateClaimPayload] =
    useState<IClaimCreatePayload>();

  const { decodedToken, isWebView } = useAppContext();

  const CurrentScreen = ScreenFlowStepMap[currentStep];

  useEffect(() => {
    return () => {
      setCurrentStep(EClaimCreateFlowSteps.SELECT_CLAIM_TYPE);
    };
  }, []);

  window.dev.i('create-claim', {
    firstMember,
    memberDetails,
  });

  const { startTimer, pauseTimer } = useDuration();

  const submitFlow = async (formState: IClaimCreateFormSchema) => {
    let payload: Partial<IClaimCreatePayload> = {
      amount: 0,
      patientId: formState.patientId ?? '',
      patientName: formState?.patientName ?? '',
      patientRelationship: formState.patientRelationship ?? null,
      source: isWebView ? 'Plum WebView' : 'Plum Web App',
      hospital: {
        hospitalid: formState.hospitalId,
        hospitalname: formState.hospitalName,
      },
      typeOfCare: formState?.typeOfCare ?? null,
      reasonForHospitalization: formState.reasonForHospitalization ?? '',
      organisationBenefitId: formState.organisationBenefitId,
      tpaName: formState.tpaName,
      typeOfBenefit: formState.typeOfBenefit,
      insuranceProviderHandle: formState.insuranceProviderHandle,
      treatmentStage: formState.treatmentStage,
      date: formState.date,
      dateFieldName: formState.dateFieldName,
      tentativeDays: formState.tentativeDays ?? null,
      typeOfClaim: formState.typeOfClaim ?? EClaimType.CASHLESS,
    };

    const BasePayloadSchema = z.object({
      memberId: z
        .string()
        .min(1)
        .default(decodedToken?.memberId ?? ''),
      memberName: z
        .string()
        .min(1)
        .default(firstMember?.name ?? ''),
      organisationId: z
        .string()
        .min(1)
        .default(firstMember?.organisationId ?? ''),
      organisationName: z
        .string()
        .min(1)
        .default(firstMember?.organisationName ?? ''),
      patientId: z.string().min(1),
      patientName: z.string().min(1),
      patientRelationship: z.nativeEnum(EPatientRelationship),
      amount: z.number().default(0),
      source: z
        .string()
        .min(1)
        .default(`Plum Web App${isWebView ? ' (WebView)' : ''}`),
      typeOfClaim: z.nativeEnum(EClaimType).default(EClaimType.CASHLESS),
      typeOfCare: z.nativeEnum(ECareType),
      reasonForHospitalization: z.string().min(1),
      communicationPreference: z
        .object({
          email: z
            .string()
            .min(1)
            .default(firstMember?.email ?? ''),
          phoneNumber: z
            .string()
            .min(1)
            .default(firstMember?.phoneNumber ?? ''),
          // alternatePhoneNumber?: string,
          // alternateEmail?: string,
        })
        .default({
          email: firstMember?.email ?? '',
          phoneNumber: firstMember?.phoneNumber ?? '',
        }),
      hospital: z.object({
        hospitalname: z.string().optional().nullable(),
        hospitalid: z.string().min(1).optional().nullable(),
      }),
      insuranceProviderHandle: z.string().min(1).nullable(),
      tpaName: z.string().min(1).nullable(),
      organisationBenefitId: z.string().min(1),
      typeOfBenefit: z.string().min(1).nullable(),
      treatmentStage: z.string().min(1),
      date: z.string().default(new Date().toISOString()),
      tentativeDays: z.string().optional().nullable(),
      dateFieldName: z.string().min(1),
    });

    const ReimbursementSchema = BasePayloadSchema.omit({
      treatmentStage: true,
      tentativeDays: true,
      hospital: true,
    });

    let CreatePayloadSchema:
      | typeof ReimbursementSchema
      | typeof BasePayloadSchema = BasePayloadSchema;

    if (
      formState.typeOfClaim === EClaimType.REIMBURSEMENT &&
      formState.benefitCategory === EBenefitCategory.EXPENSE_MANAGEMENT
    ) {
      payload = {
        ...payload,
        typeOfCare: ECareType.OPD,
        typeOfBenefit: 'health-expenses',
      };
      CreatePayloadSchema = ReimbursementSchema;
    }

    window.dev.i('create-claim VALIDATING: ', payload);
    const parsedPayload = CreatePayloadSchema.safeParse(payload);
    window.dev.i('create-claim PARSED_PAYLOAD:', parsedPayload);
    if (parsedPayload.success) {
      setCreateClaimPayload(parsedPayload.data);
      setCurrentStep(EClaimCreateFlowSteps.CLAIM_CREATED);
    } else {
      window.dev.e(
        'VALIDATION ERROR: ',
        parsedPayload?.error,
        parsedPayload?.data,
        parsedPayload.error?.errors
      );
      toast.error('Oops! Something went wrong. Please try again later.', {
        id: 'create-claim-error',
        style: {
          marginBottom: getDimensions('claims-footer').height,
        },
      });
    }
  };

  const [searchParams] = useSearchParams();

  const currentFeatureCategory = useClaimCreateStore(
    (state) => state.currentFeatureCategory
  );
  const currentPatientDetails = useClaimCreateStore(
    (state) => state.currentPatientDetails
  );
  const allBenefits = useClaimCreateStore((state) => state.allBenefits);
  const formState = useClaimCreateStore((state) => state.formState);

  const trackCreation = (
    data: IClaimDetails | null,
    attrs: Pick<IClaimCreationStats, 'creationMode'> = {
      creationMode: 'create',
    }
  ) => {
    const source = searchParams.get('utm_source');
    const timeElapsed = pauseTimer?.('CLAIM_CREATION');
    const currentTreatmentStage =
      currentFeatureCategory?.treatmentStageOptionList?.find(
        (stage) => stage.slug === formState.treatmentStage
      );
    const currentBenefit = allBenefits.get(data?.organisationBenefitId ?? '');
    const isHealthCardAvailable = !(currentBenefit?.membership?.id == null);

    const payload: IClaimCreationStats = {
      ...attrs,
      claimId: data?.plumClaimId ?? '',
      dateMode:
        (currentTreatmentStage?.tentativeDaysOptionList?.length ?? 0) > 0
          ? formState?.tentativeDays
            ? 'known'
            : 'unknown'
          : null,
      insuranceProviderHandle: data?.insuranceProviderHandle ?? '',
      hospitalId: formState?.hospitalId ?? null,
      isHealthCardAvailable,
      organisationId: currentPatientDetails?.organisationId ?? '',
      organisationName: currentPatientDetails?.organisationName ?? '',
      orgBenefitId: formState?.organisationBenefitId ?? '',
      policySelection: 'auto',
      stage: data?.stage ?? '',
      stageOfTreatment: currentTreatmentStage?.slug ?? '',
      totalDuration: timeElapsed ?? 0,
      tpaName: data?.tpaName ?? '',
      typeOfCare: formState?.typeOfCare ?? '',
      viewSource: source ?? '',
    };
    track({
      event: TRACKER_EVENTS.CC_CLAIM_CREATION_SUCCESS,
      data: payload,
    });
    sessionStorage.setItem(
      getRequestCallbackStorageKey(data?.plumClaimId ?? '') ?? '',
      true.toString()
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col h-full">
        <CurrentScreen
          isInCreateFlow
          submitFlow={submitFlow}
          createClaimPayload={createClaimPayload}
          startTimer={() => startTimer('CLAIM_CREATION')}
          pauseTimer={() => pauseTimer('CLAIM_CREATION')}
          trackCreation={trackCreation}
        />
      </div>
    </div>
  );
};

export default CreateCashlessClaim;
